/**
 * Node Actions
 *
 * Business logic for node operations.
 */

import type { CanvasEditor } from '@rool/editor';
import type { InputSystem } from '@rool/input';
import type { UINode } from '@rool/renderer';
import {
	getDescendants,
	getAutoFitSettings,
	setAutoFitFlag,
	type AutoFitSettings,
} from '@rool/editor';

/**
 * Auto-fit selected nodes to their content.
 * Filters selection to actual node IDs (excludes edge IDs).
 * Uses async measurement to support nodes with images.
 */
export async function autoFitSelection(editor: CanvasEditor): Promise<void> {
	const selected = editor.getSelection();
	const nodeIds = new Set(editor.getNodes().map((n) => n.id));
	const selectedNodeIds = selected.filter((id) => nodeIds.has(id));
	if (selectedNodeIds.length > 0) {
		await editor.autoFitNodesAsync(selectedNodeIds);
	}
}

/**
 * Get label for auto-fit command based on selection count.
 * Returns false if nothing is selected.
 */
export function getAutoFitLabel(selectedCount: number): string | false {
	if (selectedCount === 0) return false;
	if (selectedCount === 1) return 'Auto-Fit to Content';
	return `Auto-Fit ${selectedCount} Nodes`;
}

/**
 * Delete selected nodes.
 * Delegates to InputSystem's registered delete handler.
 */
export function deleteSelection(input: InputSystem): void {
	input.executeCommand('edit.delete');
}

/**
 * Select all nodes and edges.
 */
export function selectAll(editor: CanvasEditor): void {
	editor.selectAll();
}

/**
 * Clear selection.
 */
export function clearSelection(editor: CanvasEditor): void {
	editor.clearSelection();
}

/**
 * Get label for delete command based on selection count.
 * Returns false if nothing is selected.
 */
export function getDeleteLabel(selectedCount: number): string | false {
	if (selectedCount === 0) return false;
	if (selectedCount === 1) return 'Delete';
	return `Delete ${selectedCount} Items`;
}

/**
 * Get label for duplicate command based on selection count.
 * Returns false if nothing is selected.
 */
export function getDuplicateLabel(selectedCount: number): string | false {
	if (selectedCount === 0) return false;
	if (selectedCount === 1) return 'Duplicate';
	return `Duplicate ${selectedCount} Items`;
}

/**
 * Select descendants of all currently selected nodes.
 * Adds the descendant nodes to the current selection.
 */
export function selectDescendants(editor: CanvasEditor): void {
	const selected = editor.getSelection();
	if (selected.length === 0) return;

	const edges = editor.getEdges();
	const allIds = new Set(selected);
	for (const nodeId of selected) {
		for (const id of getDescendants(edges, nodeId)) {
			allIds.add(id);
		}
	}
	editor.select([...allIds]);
}

/**
 * Get label for select-descendants command based on selection count.
 * Returns false if nothing is selected.
 */
export function getSelectDescendantsLabel(
	selectedCount: number,
): string | false {
	if (selectedCount === 0) return false;
	return 'Select Descendants';
}

// =============================================================================
// AutoFit Flag Toggles
// =============================================================================

/**
 * Get the checked state of an autofit flag across selected nodes.
 * Returns true if all have it on, false if all have it off, 'mixed' otherwise.
 */
export function getAutoFitFlagChecked(
	selectedNodes: UINode[],
	flag: keyof AutoFitSettings,
): boolean | 'mixed' {
	if (selectedNodes.length === 0) return false;
	let allOn = true;
	let allOff = true;
	for (const node of selectedNodes) {
		const settings = getAutoFitSettings(node as any);
		if (settings[flag]) allOff = false;
		else allOn = false;
	}
	if (allOn) return true;
	if (allOff) return false;
	return 'mixed';
}

/**
 * Toggle an autofit flag on all selected nodes.
 * If mixed or all-on, turns off. If all-off, turns on.
 */
export async function toggleAutoFitFlag(
	editor: CanvasEditor,
	flag: keyof AutoFitSettings,
	persist: (nodeId: string, data: Record<string, unknown>) => Promise<void>,
): Promise<void> {
	const selected = editor.getSelection();
	const allNodes = editor.getNodes();
	const selectedNodes = allNodes.filter((n) => selected.includes(n.id));
	if (selectedNodes.length === 0) return;

	// Determine new value: if any are on, turn all off; otherwise turn all on
	const currentState = getAutoFitFlagChecked(
		selectedNodes.map(
			(n) =>
				({
					id: n.id,
					type: (n as any).type,
					data: (n as any).data ?? {},
				}) as any,
		),
		flag,
	);
	const newValue = currentState === false;

	const persistPromises: Promise<void>[] = [];

	for (const node of selectedNodes) {
		const data = (node as any).data as Record<string, unknown> | undefined;
		if (!data) continue;
		const obj = (data.obj as Record<string, unknown>) ?? data;
		setAutoFitFlag(obj, flag, newValue);

		// Persist flags AND current dimensions to prevent needsAutoFit
		// from re-triggering after the store rebuild.
		// Only include flags that are explicitly set (typeof boolean) to avoid
		// sending undefined→null which deletes fields in the SDK.
		const persistData: Record<string, unknown> = {
			_width: node.size.width,
			_height: node.size.height,
		};
		if (typeof obj.autoFitWidth === 'boolean')
			persistData.autoFitWidth = obj.autoFitWidth;
		if (typeof obj.autoFitHeight === 'boolean')
			persistData.autoFitHeight = obj.autoFitHeight;
		if (typeof obj.autoSizeText === 'boolean')
			persistData.autoSizeText = obj.autoSizeText;
		if (typeof obj.wordWrap === 'boolean') persistData.wordWrap = obj.wordWrap;

		persistPromises.push(
			persist(node.id, persistData).catch((err) =>
				console.error('[autofit] Failed to persist flags:', err),
			),
		);
	}

	// Await all persists so the SDK has committed data before autofit or
	// further rebuilds can race with stale server subscription patches.
	await Promise.all(persistPromises);

	// If we enabled an autofit axis, re-run autofit
	if (newValue && (flag === 'autoFitWidth' || flag === 'autoFitHeight')) {
		const nodeIds = selectedNodes.map((n) => n.id);
		await editor.autoFitNodesAsync(nodeIds);
	}

	editor.render();
}
